/**
 * PDF plan viewer: page navigation and zoom over a canvas.
 * Owns no pdf.js types beyond the PdfHandle facade.
 */
import { el, mount, setVisible } from "../components/dom";
import { PDF_VIEW } from "../../core/config";
import { logger } from "../../core/logger";
import type { PdfHandle } from "../../pdf/pdf-document";

export interface PdfPanel {
  readonly root: HTMLElement;
  attach(handle: PdfHandle): void;
  setVisible(visible: boolean): void;
}

export function createPdfPanel(): PdfPanel {
  // pt-20 clears the mode bar, which is pinned top-centre over this panel.
  const root = el(
    "div",
    "absolute inset-0 flex flex-col items-center gap-3 overflow-auto p-4 pt-20",
  );
  setVisible(root, false);

  const canvas = el("canvas", "glass max-w-full rounded-[14px]");
  const toolbar = el(
    "div",
    "glass glass-hover flex items-center gap-2 px-3 py-2 sticky top-0 z-10",
  );

  const prev = button("‹");
  const next = button("›");
  const zoomOut = button("−");
  const zoomIn = button("+");
  const label = el("span", "text-xs text-[var(--color-ink-300)] min-w-[86px] text-center", "—");

  mount(toolbar, prev, label, next, divider(), zoomOut, zoomIn);
  mount(root, toolbar, canvas);

  let handle: PdfHandle | null = null;
  let page = 1;
  let scale: number = PDF_VIEW.BASE_SCALE;

  function refresh(): void {
    if (handle === null) return;
    const current = handle;
    label.textContent = `${String(page)} / ${String(current.pageCount)}`;
    prev.disabled = page <= 1;
    next.disabled = page >= current.pageCount;
    zoomOut.disabled = scale <= PDF_VIEW.MIN_SCALE;
    zoomIn.disabled = scale >= PDF_VIEW.MAX_SCALE;

    current.renderPage(page, canvas, scale).catch((cause: unknown) => {
      logger.error("PDF page render failed", cause);
    });
  }

  prev.addEventListener("click", () => {
    if (page > 1) {
      page -= 1;
      refresh();
    }
  });
  next.addEventListener("click", () => {
    if (handle !== null && page < handle.pageCount) {
      page += 1;
      refresh();
    }
  });
  zoomIn.addEventListener("click", () => {
    scale = Math.min(scale + PDF_VIEW.ZOOM_STEP, PDF_VIEW.MAX_SCALE);
    refresh();
  });
  zoomOut.addEventListener("click", () => {
    scale = Math.max(scale - PDF_VIEW.ZOOM_STEP, PDF_VIEW.MIN_SCALE);
    refresh();
  });

  return {
    root,
    attach(next_) {
      handle = next_;
      page = 1;
      scale = PDF_VIEW.BASE_SCALE;
      refresh();
    },
    setVisible: (visible) => {
      setVisible(root, visible);
    },
  };
}

function button(glyph: string): HTMLButtonElement {
  const node = el("button", "btn", glyph);
  node.type = "button";
  return node;
}

function divider(): HTMLElement {
  return el("span", "w-px h-4 bg-white/15");
}
